import React, {useState} from 'react';
import {Button, Form, Modal, Spinner} from "react-bootstrap";
import Select from "react-select";
import {CrmPermission, UserRole, IEditUserModalProps, IEditUserForm, IRoleAssignment, CRM_ROLE_LABELS} from "../models";
import {UsersService} from "../Users.service";
import { isNullOrUndefined } from 'common/data-util';
import { onNumberChange } from 'helpers';

const CRM_PERMISSION_LABELS: Record<CrmPermission, string> = {
    [CrmPermission.ASSIGN_CUSTOMER_SALES_MANAGER]: "Kliendile müügijuhi määramine",
    [CrmPermission.MANAGE_OTHER_SALES_MANAGER_SUBSTITUTIONS]: "Haldab teiste müügijuhtide asendusi",
    [CrmPermission.EMAIL_TEMPLATE_EDITOR]: "E-kirjade mallide muutmine"
};

const CRM_ROLES: UserRole[] = [UserRole.SALES_LEAD, UserRole.SALES_MANAGER];

const SALES_LEAD_DEFAULT_APPROVAL_LIMIT = 50000;

const CRM_ROLE_OPTIONS: { value: UserRole | undefined; label: string } [] = [
    { value: undefined, label: "Puudub" },
    ...CRM_ROLES.map(role => ({value: role, label: CRM_ROLE_LABELS[role]}))
];

function initialCrmPermissions(permissions: string[]): string[] {
    return Object.values(CrmPermission).filter(permission => permissions.includes(permission));
}

export function EditUserModal(props: IEditUserModalProps) {
    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);
    const [validated, setValidated] = useState(false);
    const [form, setForm] = useState<IEditUserForm>({
        adminActive: props.editingUser?.adminActive ?? false,
        crmRole: CRM_ROLES.find(role => props.editingUser.roles.includes(role)),
        crmPermissions: Object.values(CrmPermission).filter(permission => props.editingUser.permissions.includes(permission)),
        approvalLimit: props.editingUser?.approvalLimit
    });

    const updateForm = (changes: Partial<IEditUserForm>) => setForm(current => ({...current, ...changes}));

    const initialPermissions = initialCrmPermissions(props.editingUser?.permissions);
    const hasCrmRole = !!form.crmRole;

    const approvalLimitInvalid = hasCrmRole && (isNullOrUndefined(form.approvalLimit) || form.approvalLimit! < 0);

    const buildRoles = (): IRoleAssignment[] => {
        if (!form.crmRole) return [];

        return [{
            roleCode: form.crmRole,
            permissions: form.crmRole === UserRole.SALES_LEAD ? form.crmPermissions : []
        }];
    };

    const onRoleChange = (role: UserRole | undefined) => {
        updateForm({
            crmRole: role,
            crmPermissions: role === UserRole.SALES_LEAD ? initialPermissions : [],
            approvalLimit: role === UserRole.SALES_LEAD ? (form.approvalLimit ?? SALES_LEAD_DEFAULT_APPROVAL_LIMIT): form.approvalLimit
        });
    };

    const togglePermission = (permission: CrmPermission, checked: boolean) => {
        updateForm({
            crmPermissions: checked
                ? [...form.crmPermissions, permission]
                : form.crmPermissions.filter(p => p !== permission)
        });
    };

    const handleSubmit = async () => {
        setValidated(true);

        if (approvalLimitInvalid) return;

        setSaving(true);
        setSavingError(false);

        try {
            await UsersService.patchUser(props.editingUser.id, form.adminActive, buildRoles(), hasCrmRole ? form.approvalLimit : undefined);
            setTimeout(() => {
                props.submitCloseHandler();
            }, 300);
        } catch(e) {
            console.error('Saving failed', e);
            setSavingError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            show
            onHide={props.rejectCloseHandler}
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    Kasutaja andmete muutmine
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form onSubmit={(e: any) => e.preventDefault()}
                      id={"user-form"}
                      className={'w-100'}
                >
                    <div className='row mb-2'>
                        <div className="col-5">
                            Kasutaja nimi
                        </div>
                        <div className="text-start font-weight-bold">
                            {props.editingUser.name}
                        </div>
                    </div>
                    <div className='row mb-2'>
                        <div className="col-5">
                            Isikukood
                        </div>
                        <div className="text-start font-weight-bold">
                            {props.editingUser.personalIdentificationCode}
                        </div>
                    </div>
                    <Form.Group controlId={'form-admin-active'} className="row">
                        <Form.Label className={'col-5'}>
                            <span className="text-nowrap">Staatus</span>
                        </Form.Label>
                        <Form.Check
                            inline
                            className="form-check-label-black"
                            label="Aktiivne"
                            type="checkbox"
                            checked={form.adminActive}
                            disabled={saving}
                            onChange={(e: any) => updateForm({adminActive: e.target.checked})}
                        />
                    </Form.Group>
                    <hr className="my-3"/>
                    <h6 className="font-weight-bold mb-2">CRM</h6>
                    <Form.Group controlId={'form-crm-role'} className="row mt-2">
                        <Form.Label className={'col-5'}>
                            <span className="text-nowrap">Roll</span>
                        </Form.Label>
                        <div className="col p-0">
                            <Select
                                inputId={'crm-role-select'}
                                options={CRM_ROLE_OPTIONS}
                                value={CRM_ROLE_OPTIONS.find(opt => opt.value === form.crmRole)}
                                isDisabled={saving}
                                placeholder={'Vali roll'}
                                onChange={(opt: any) => onRoleChange((opt?.value ?? undefined) as UserRole | undefined)}
                            />
                        </div>
                    </Form.Group>
                    { hasCrmRole &&
                        <Form.Group controlId={'form-crm-approval-limit'} className="row mt-2">
                            <Form.Label className={'col-5'}>
                                <span className="text-nowrap">Kinnitamise limiit (€)</span>
                            </Form.Label>
                            <div className="col p-0">
                                <Form.Control
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.approvalLimit ?? ""}
                                    disabled={saving}
                                    placeholder={'Sisesta limiit'}
                                    isInvalid={validated && approvalLimitInvalid}
                                    onChange={(e: any) => updateForm({approvalLimit: onNumberChange(e)})}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Sisesta korrektne limiit (0 või suurem).
                                </Form.Control.Feedback>
                            </div>
                        </Form.Group>
                    }
                    { form.crmRole === UserRole.SALES_LEAD &&
                        <Form.Group controlId={'form-crm-permissions'} className="row mt-2">
                            <Form.Label className="col-12 mb-1">
                                <span className="text-nowrap">Õigused</span>
                            </Form.Label>
                            <div className="col-12">
                                { Object.values(CrmPermission).map(permission => (
                                    <Form.Check
                                        key={permission}
                                        id={`crm-permission-${permission}`}
                                        className="form-check-label-black w-fit"
                                        label={CRM_PERMISSION_LABELS[permission]}
                                        type="checkbox"
                                        checked={form.crmPermissions.includes(permission)}
                                        disabled={saving}
                                        onChange={(e: any) => togglePermission(permission, e.target.checked)}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    }
                </Form>
            </Modal.Body>

            <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                <Button variant="info"
                        onClick={() => props.rejectCloseHandler()}
                        type="button"
                        className={"d-flex align-items-center"}>
                    <span className='px-2'>Tühista</span>
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    type="button"
                    disabled={saving}
                    className="d-flex align-items-center"
                >
                    {
                        !saving &&
                        <span className='px-2'>Salvesta</span>
                    }
                    {
                        saving &&
                        <>
                            <span className='px-2'>Salvestab…</span>
                            <Spinner size={"sm"} animation={"border"}/>
                        </>
                    }
                </Button>

            </Modal.Footer>
            {
                !saving && savingError &&
                <span className="d-flex justify-content-end text-danger px-2 pb-2">
                    Viga salvestamisel, proovi mõne hetke pärast uuesti.
                </span>
            }
        </Modal>
    );
}
